import React, { FC, useCallback, useEffect, useState } from 'react'

import { useFocusNodeById } from '@please/lrud'
import type { Swiper as SwiperType } from 'swiper'

import { Slider } from '@/components/Slider/Slider'
import { getActiveSlideIndex } from '@/components/Slider/SliderHelpers'
import { EmptyTile } from '@/components/Tiles/EmptyTile'
import { sliderMovementType } from '@/constants/common'
import { limitDefaultTilesPerLine } from '@/constants/tiles'
import { useOpenTile } from '@/hooks/useOpenTile'
import useStore from '@/store/useStore'
import { LineProps } from '@/types/componentsPropsTypes'
import { isPointerMode } from '@/utils/utils'

import {
  useUpdateSliderData,
  useRefreshSliderDataByNearestEventTime,
  useLinePointerNavigation,
  useVisiableSliderNavigation,
} from './hooks'
import {
  LineElem,
  LineTitle,
  NextNavigationSection,
  PrevNavigationSection,
  SliderWrapper,
} from './styles'
import { getTileProps } from './utils'

export const Line: FC<LineProps> = ({
  nodeId,
  lineTitle,
  lineData,
  isEventPage,
  isPopup,
  isTitleHidden,
  allowViewAllTile = false,
  initFocusId,
  categoryId,
}) => {
  const focusNode = useFocusNodeById(nodeId)

  const listPopup = useStore((state) => state.common.listPopup)
  const currentEvent = useStore((state) => state.common.currentEvent)
  const limitTilesPerLine = useStore(
    (state) => state.common.appConfig.limitTilesPerLine,
  )

  const [isVisible, setIsVisible] = useState(false)
  const [activeSlideIndex, setActiveSlideIndex] = useState(0)
  const [swiper, setSwiper] = useState<Nullable<SwiperType>>(null)

  const isVirtual = limitTilesPerLine > limitDefaultTilesPerLine

  const slidesPerView = getTileProps(
    lineData.tileShape,
    lineData.tileSize,
  ).tilesPerView

  const initSlideId = initFocusId?.split('_').reverse()[1]

  const { sliderData, updateSliderData } = useUpdateSliderData(
    lineData,
    allowViewAllTile,
  )

  const activeSlideId =
    activeSlideIndex !== undefined ? sliderData[activeSlideIndex]?.id : ''

  useRefreshSliderDataByNearestEventTime(sliderData, updateSliderData)
  const { isNextArrowVisible, isPrevArrowVisible, updateArrowVisiable } =
    useVisiableSliderNavigation({
      nodeId,
      swiper,
      sliderData,
      slidesPerView,
    })

  useEffect(() => {
    /**
     * Show lines with allowHideIfEmpty = false
     */
    setIsVisible(!lineData.allowHideIfEmpty)

    return () => {
      swiper?.destroy(true, true)
    }
  }, [])

  useEffect(() => {
    if (activeSlideIndex !== undefined) {
      focusNode?.isFocused && updateArrowVisiable()
    }
  }, [activeSlideIndex])

  useEffect(() => {
    if (!initSlideId || !swiper) {
      return
    }

    const activeIndex = getActiveSlideIndex(sliderData, initSlideId)
    swiper.slideTo(activeIndex)
    setActiveSlideIndex(activeIndex)
  }, [initSlideId, swiper])

  useEffect(() => {
    if (
      !isEventPage ||
      listPopup ||
      !currentEvent ||
      !swiper ||
      !sliderData.length
    ) {
      return
    }

    const currentIndex = sliderData.findIndex(
      ({ data }) => data?.event?.id === currentEvent.id,
    )
    const activeIndex = currentIndex <= 0 ? 0 : currentIndex
    swiper.slideTo(activeIndex)
    setActiveSlideIndex(activeIndex)
  }, [listPopup, sliderData, currentEvent, swiper])

  const onSwiperHandler = useCallback(
    (swiper: SwiperType) => {
      /**
       * Important. Place init events later in queue
       */
      setTimeout(() => {
        setSwiper(swiper)
        setIsVisible(true)
      }, 0)
    },
    [setSwiper],
  )

  const slideNext = (e?: LRUDEvent) => {
    e?.preventDefault()
    swiper && setActiveSlideIndex(swiper?.activeIndex)
  }

  const slidePrev = (e?: LRUDEvent) => {
    if (swiper && swiper.activeIndex >= 0 && activeSlideIndex) {
      e?.preventDefault()
      swiper && setActiveSlideIndex(swiper?.activeIndex)
    }
  }

  const handleTileSelected = useOpenTile({
    shouldNavigateToEventUrl: !isPopup && isEventPage,
    listContextId: lineData.sliderId,
    isSearchListType: lineData.isSearchListType,
    name: lineTitle,
    tileShape: lineData.tileShape,
    searchResultType: lineData.searchResultType,
    // openWatchableEventPage: isEventPage,
  })

  const { startSlideNavigation, stopSlideNavigation, onMouseOverTile } =
    useLinePointerNavigation({
      nodeId,
      swiper,
      setActiveSlideIndex: setActiveSlideIndex,
    })

  /**
   * @description
   * Always display the 'LineTitle' to add extra space in a focused Tile, hide only text node.
   */
  const renderLineTitle = () => {
    const titleText = isTitleHidden ? null : lineTitle || null
    return <LineTitle>{titleText}</LineTitle>
  }

  const onSelected = () => {
    const tileData = sliderData[activeSlideIndex]
    if (!tileData) {
      return
    }
    const focusId = `${nodeId}_${tileData.id}_${activeSlideIndex}`

    handleTileSelected({
      tileData,
      focusId,
      anchorId: lineData.sliderId,
      tileIndex: activeSlideIndex,
      lineId: nodeId,
    })
  }

  return (
    <LineElem
      elementType="section"
      focusId={nodeId}
      className="line"
      onRight={slideNext}
      onLeft={slidePrev}
      $isVisible={isVisible}
      onSelected={onSelected}
      $tileShape={lineData.tileShape}
      $tileSize={lineData.tileSize}
    >
      {renderLineTitle()}
      {sliderData.length > 0 ? (
        <SliderWrapper>
          <Slider
            swiper={swiper}
            activeSlideId={activeSlideId}
            setActiveSlideIndex={setActiveSlideIndex}
            slidesPerView={slidesPerView}
            sliderId={nodeId}
            tileShape={lineData.tileShape}
            tileSize={lineData.tileSize}
            sliderData={sliderData}
            onSwiper={onSwiperHandler}
            initialSlide={activeSlideIndex}
            slidesOffsetAfter={
              getTileProps(lineData.tileShape, lineData.tileSize)
                .slidesOffsetAfter
            }
            movementType={sliderMovementType.PUSH}
            setVirtual={isVirtual}
            onMouseOverTile={onMouseOverTile}
            initSlideId={initSlideId}
            categoryId={categoryId}
          />
          {isPointerMode && (
            <>
              <PrevNavigationSection
                onMouseEnter={startSlideNavigation}
                onMouseOut={stopSlideNavigation}
                $isVisible={isPrevArrowVisible}
              />
              <NextNavigationSection $isVisible={isNextArrowVisible} />
            </>
          )}
        </SliderWrapper>
      ) : (
        <EmptyTile />
      )}
    </LineElem>
  )
}
